import { useMemo } from 'react'
import { useGetSustainabilityData } from 'domains/home/dashboard-analysis-sustainability/hooks/useGetSustainabilityData'
import { Chart } from '@77sol-ui/molecules'
import { useToast } from '@77sol/core'
import { lineStyles } from './constants'
import { ChartContainer, Wrapper } from './styles'
import { type IFilters } from 'views/Home/components/Performance/types'

import { formatDataToChartJs } from '../../utils/formatDataToChartJs'

import { useCustomizeLabels } from './hooks/useCustomizeLabels'
import { useCalculateTicksStepSize } from './hooks/useCalculateTicksStepSize'
import { CircularProgress } from 'components'
import { Legend } from '../Legend'
import useWindowSize from 'hooks/useWindowSize'

export interface ISustainabilityChartProps {
  filters: Omit<IFilters, 'chart'>
}

export function SustainabilityChart({ filters }: ISustainabilityChartProps) {
  const { onErrorOpenToast } = useToast()
  const { width } = useWindowSize()
  const isMobile = width <= 768

  const { data, isLoading } = useGetSustainabilityData({
    params: filters,
    onError: () => {
      onErrorOpenToast('Erro ao buscar os dados de sustentabilidade')
    },
  })

  const formattedData = useMemo(() => {
    return data && !isLoading && formatDataToChartJs({ lineStyles, data })
  }, [data, isLoading])

  const { y1StepSize, yStepSize, maxTicksYLimit } = useCalculateTicksStepSize({
    maxYValue: formattedData?.maxValues?.y,
    maxY1Value: formattedData?.maxValues?.y1,
    gap: 7,
  })

  const {
    customizePowerAxisLabel,
    customizeTonAxisLabel,
    customizeDataAxisLabel,
    customizeTooltipTitle,
    customizeTooltipLabel,
  } = useCustomizeLabels({
    period: filters.period,
    data,
    units: formattedData?.units,
  })

  if (isLoading) {
    return (
      <ChartContainer>
        <CircularProgress text="Carregando..." />
      </ChartContainer>
    )
  }

  return (
    <Wrapper>
      <Legend
        text="Proposta"
        color="green"
        maxWidth="293px"
        description="Quantidade total de Potência (kWp) originada em suas propostas, e o
          impacto positivo delas na redução de emissões de CO2 no mundo
          (projeção de 30 anos em toneladas)"
      />

      <ChartContainer>
        <Chart.Line
          data={formattedData?.data || []}
          labels={formattedData?.labels || []}
          lineRadius={0.3}
          plugins={[Chart.Plugins.crosshair, Chart.Plugins.legendSpacing]}
          twoYAxis
          maintainAspectRatio={false}
          showLegends={false}
          yStepSize={yStepSize}
          y1StepSize={y1StepSize}
          maxTicksLimit={7}
          maxTicksYLimit={maxTicksYLimit}
          options={{
            clip: false,
          }}
          y1GridColor="transparent"
          customizeYTicks={customizePowerAxisLabel}
          customizeY1Ticks={customizeTonAxisLabel}
          customizeXTicks={customizeDataAxisLabel}
          customizeTooltip={{
            title: customizeTooltipTitle,
            label: customizeTooltipLabel,
          }}
          tiltLabels={isMobile}
        />
      </ChartContainer>
    </Wrapper>
  )
}
